import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProcessPhase from './components/ProcessPhase';
import CommunicationWorkflow from './components/CommunicationWorkflow';
import ProjectEstimator from './components/ProjectEstimator';
import QualityAssurance from './components/QualityAssurance';
import CTASection from '../homepage/components/CTASection';

const Process = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const processPhases = [
    {
      icon: "Lightbulb",
      title: "Discovery & Strategy",
      description: "Deep-dive into your business objectives, technical requirements, and success metrics to architect the optimal solution pathway.",
      duration: "1-2 weeks",
      deliverables: [
        "Comprehensive requirements documentation",
        "Technical architecture blueprint",
        "Project roadmap with milestone definitions",
        "Risk assessment and mitigation plan",
        "Technology stack recommendations"
      ],
      clientInvolvement: "Daily stakeholder interviews, workshop sessions, and collaborative requirement refinement ensuring complete alignment on project vision and success criteria.",
      tools: ["Miro", "Figma", "Notion", "Lucidchart"]
    },
    {
      icon: "Palette",
      title: "Design & Prototyping",
      description: "Transform strategic vision into tangible user experiences through iterative design processes and interactive prototypes.",
      duration: "2-3 weeks",
      deliverables: [
        "User journey maps and personas",
        "Wireframes for all key screens",
        "High-fidelity UI mockups",
        "Interactive clickable prototype",
        "Design system and component library"
      ],
      clientInvolvement: "Weekly design review sessions with feedback incorporation, usability testing participation, and approval checkpoints before development handoff.",
      tools: ["Figma", "Adobe XD", "Sketch", "InVision"]
    },
    {
      icon: "Code2",
      title: "Development Sprints",
      description: "Agile development methodology with two-week sprint cycles delivering working software incrementally for continuous feedback.",
      duration: "8-16 weeks",
      deliverables: [
        "Production-ready codebase with documentation",
        "API integrations and backend services",
        "Database schema and data models",
        "Admin dashboard and management tools",
        "Automated test suites and CI/CD pipelines"
      ],
      clientInvolvement: "Bi-weekly sprint demos showcasing completed features, daily async progress updates, and collaborative prioritization of upcoming sprint work.",
      tools: ["React", "Node.js", "PostgreSQL", "AWS", "Docker"]
    },
    {
      icon: "TestTube2",
      title: "Quality Assurance",
      description: "Rigorous multi-layered testing ensuring your application meets enterprise-grade standards for reliability and performance.",
      duration: "2-3 weeks",
      deliverables: [
        "Comprehensive test coverage reports",
        "Security audit and penetration testing results",
        "Performance optimization recommendations",
        "Cross-browser and device compatibility validation",
        "Accessibility compliance certification"
      ],
      clientInvolvement: "User acceptance testing coordination, bug triage prioritization, and final approval sign-off before production deployment.",
      tools: ["Jest", "Cypress", "Selenium", "Lighthouse", "SonarQube"]
    },
    {
      icon: "Rocket",
      title: "Deployment & Launch",
      description: "Seamless production deployment with zero-downtime strategies and comprehensive launch support ensuring smooth go-live.",
      duration: "1 week",
      deliverables: [
        "Production environment setup and configuration",
        "Database migration and data seeding",
        "SSL certificates and security hardening",
        "Monitoring and alerting system setup",
        "Launch day support and incident response"
      ],
      clientInvolvement: "Launch readiness review, go/no-go decision participation, and real-time communication during deployment window.",
      tools: ["AWS", "Vercel", "Docker", "Kubernetes", "DataDog"]
    },
    {
      icon: "Headphones",
      title: "Support & Optimization",
      description: "Post-launch partnership ensuring your application continues to perform optimally with ongoing maintenance and enhancements.",
      duration: "Ongoing",
      deliverables: [
        "24/7 monitoring and incident response",
        "Monthly performance optimization reports",
        "Security patches and dependency updates",
        "Feature enhancement roadmap planning",
        "Technical support and troubleshooting"
      ],
      clientInvolvement: "Monthly business review meetings, feature request prioritization, and strategic planning for future development phases.",
      tools: ["Jira", "Slack", "PagerDuty", "Google Analytics", "Hotjar"]
    }
  ];

  const contingencyPlans = [
    {
      icon: "Shield",
      title: "Technical Contingencies",
      description: "Backup technology alternatives and architectural pivots prepared for unexpected technical challenges or third-party service disruptions."
    },
    {
      icon: "Users",
      title: "Resource Flexibility",
      description: "Cross-trained team members and scalable resource allocation ensuring project continuity despite personnel changes or capacity constraints."
    },
    {
      icon: "Calendar",
      title: "Timeline Buffers",
      description: "Strategic buffer time allocation in project schedules accommodating scope refinements and unforeseen complexity discoveries."
    },
    {
      icon: "DollarSign",
      title: "Budget Management",
      description: "Transparent change request process with cost impact analysis ensuring financial predictability and no surprise budget overruns."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Our Development Process | Devontis Technologies</title>
        <meta name="description" content="Transparent development methodology combining agile practices with strategic thinking. Discover how Devontis Technologies delivers exceptional SaaS solutions through proven processes." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <section className="bg-gradient-to-br from-primary/5 to-accent/5 py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* <Breadcrumbs /> */}
              
              <div className="max-w-4xl">
                <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-headline font-bold text-foreground mb-4 md:mb-6">
                  Our Development Process
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed mb-6 md:mb-8">
                  Transparent methodology combining agile development practices with strategic thinking. 
                  We transform complex technical possibilities into clear business outcomes through proven processes 
                  that reduce anxiety and build confidence in your digital transformation journey.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={() => window.location.href = '/contact'}
                  >
                    Schedule Consultation
                  </Button>
                  {/* <Button
                    variant="outline"
                    size="lg"
                    iconName="FileText"
                    iconPosition="left"
                  >
                    Download Process Guide
                  </Button> */}
                </div>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-8 md:mb-12 lg:mb-16">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-headline font-bold text-foreground mb-3 md:mb-4">
                  Six-Phase Development Journey
                </h2>
                <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                  From initial discovery to ongoing optimization, our structured approach ensures predictable outcomes 
                  and exceptional results at every stage of your project.
                </p>
              </div>

              <div className="max-w-5xl mx-auto">
                {processPhases?.map((phase, index) => (
                  <ProcessPhase 
                    key={index}
                    phase={phase}
                    index={index}
                    isLast={index === processPhases?.length - 1}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <CommunicationWorkflow />
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <ProjectEstimator />
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <QualityAssurance />
            </div>
          </section>

          <section className="py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-card border border-border rounded-lg p-6 md:p-8 lg:p-10">
                <div className="text-center mb-8 md:mb-12">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-headline font-bold text-foreground mb-3 md:mb-4">
                    Contingency Planning
                  </h2>
                  <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto">
                    Proactive risk management strategies ensuring project success despite unexpected challenges or changing circumstances.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
                  {contingencyPlans?.map((plan, index) => (
                    <div 
                      key={index}
                      className="bg-background border border-border rounded-lg p-4 md:p-6 hover:border-primary transition-all duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 md:w-14 md:h-14 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name={plan?.icon} size={24} color="var(--color-primary)" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
                            {plan?.title}
                          </h3>
                          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                            {plan?.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

         <CTASection />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Process;